// src/components/Navbar.js
// Full-width top navbar — website chrome (logo, nav links, auth/CTA), not an
// app-dashboard sidebar. Sticky, glassy, matches the openart.ai-style shell.

import { useEffect, useRef, useState } from "react";
import Logo from "./Logo";
import Avatar from "./Avatar";

// "Home" is the signed-in landing surface, so it only appears once there is
// an account to land on — logged-out visitors get the marketing page instead.
const LINKS = [
  { id: "home", label: "Home", authOnly: true },
  { id: "drift", label: "Explore" },
  { id: "characters", label: "Characters" },
  { id: "assistant", label: "Assistant" },
  { id: "subscribe", label: "Pricing" },
];

export default function Navbar({ active, onNavigate, user, unread = 0, onLogin, onSignup, onLogout, onNotifications, onProfile }) {
  const links = LINKS.filter((l) => !l.authOnly || user);
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const createRef = useRef(null);

  useEffect(() => {
    if (!createOpen) return;
    const onDocClick = (e) => {
      if (createRef.current && !createRef.current.contains(e.target)) setCreateOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [createOpen]);

  const go = (id) => {
    setOpen(false);
    setCreateOpen(false);
    onNavigate(id);
  };

  const createActive = active === "image" || active === "video" || active === "studio";

  return (
    <header className="navbar">
      <div className="navbar__inner">
        <Logo size={28} onClick={() => go(user ? "home" : "landing")} />

        <nav className="navbar__links">
          <div className="navbar__create" ref={createRef}>
            <button
              className={`navbar__link ${createActive ? "navbar__link--active" : ""}`}
              onClick={() => setCreateOpen((o) => !o)}
            >
              Create <span className="navbar__caret">▾</span>
            </button>
            {createOpen && (
              <div className="navbar__dropdown">
                <button className="navbar__dropdown-item" onClick={() => go("image")}>
                  <span className="navbar__dropdown-icon">🖼️</span>
                  <span>
                    <b>Image</b>
                    <small>Generate &amp; edit images</small>
                  </span>
                </button>
                <button className="navbar__dropdown-item" onClick={() => go("video")}>
                  <span className="navbar__dropdown-icon">🎬</span>
                  <span>
                    <b>Video</b>
                    <small>Direct cinematic video</small>
                  </span>
                </button>
                <button className="navbar__dropdown-item" onClick={() => go("studio")}>
                  <span className="navbar__dropdown-icon">🎞️</span>
                  <span>
                    <b>Studio</b>
                    <small>Full pro video editor</small>
                  </span>
                </button>
              </div>
            )}
          </div>

          {links.map((l) => (
            <button
              key={l.id}
              className={`navbar__link ${active === l.id ? "navbar__link--active" : ""}`}
              onClick={() => go(l.id)}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="navbar__actions">
          {user ? (
            <>
              <button className="navbar__credits" onClick={() => go("credits")} title="Credits">
                💎 {Math.floor(user.credits ?? 0)}
              </button>
              <button className="navbar__icon" title="Notifications" onClick={onNotifications}>
                🔔{unread > 0 && <span className="navbar__badge">{unread > 9 ? "9+" : unread}</span>}
              </button>
              <button className="navbar__avatar" title="Profile & settings" onClick={onProfile}>
                <Avatar name={user.username} url={user.avatar_url} size={34} />
              </button>
              <button className="btn btn--ghost navbar__logout-desktop" onClick={onLogout}>Log out</button>
            </>
          ) : (
            <>
              <button className="btn btn--ghost navbar__signin" onClick={onLogin}>Sign in</button>
              <button className="btn btn--primary" onClick={onSignup}>Get Started</button>
            </>
          )}
          <button className="navbar__burger" aria-label="Menu" onClick={() => setOpen((o) => !o)}>
            {open ? "✕" : "☰"}
          </button>
        </div>
      </div>

      {open && (
        <div className="navbar__mobile">
          <div className="navbar__mobile-label">Create</div>
          <button className="navbar__mobile-link" onClick={() => go("image")}>🖼️ Image</button>
          <button className="navbar__mobile-link" onClick={() => go("video")}>🎬 Video</button>
          <button className="navbar__mobile-link" onClick={() => go("studio")}>🎞️ Studio</button>

          {links.map((l) => (
            <button
              key={l.id}
              className={`navbar__mobile-link ${active === l.id ? "navbar__link--active" : ""}`}
              onClick={() => go(l.id)}
            >
              {l.label}
            </button>
          ))}
          {user ? (
            <>
              <button className="navbar__mobile-link" onClick={() => go("drops")}>My Drops</button>
              <button className="navbar__mobile-link" onClick={() => go("profile")}>Profile</button>
              <button className="navbar__mobile-link" onClick={onLogout}>Log out</button>
            </>
          ) : (
            <>
              <button className="navbar__mobile-link" onClick={onLogin}>Sign in</button>
              <button className="navbar__mobile-link" onClick={onSignup}>Get Started</button>
            </>
          )}
        </div>
      )}
    </header>
  );
}
